import { useState, useEffect } from 'react'
import { supabase } from '../../../lib/supabase'
import type { ComponentType } from '../../../types/builder.types'

interface Component {
  id: string
  name: string
  type: ComponentType
  data_source: string
  is_published: boolean
  version: number
  created_at: string
  updated_at: string
}

interface ComponentListProps {
  onCreateNew: () => void
  onEditComponent: (componentId: string) => void
}

export function ComponentList({ onCreateNew, onEditComponent }: ComponentListProps) {
  const [components, setComponents] = useState<Component[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all')
  const [typeFilter, setTypeFilter] = useState<ComponentType | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    loadComponents()
  }, [])

  const loadComponents = async () => {
    try {
      setLoading(true)
      setError(null)

      // @ts-ignore - TypeScript type issue with components table
      const { data, error: fetchError } = await supabase
        .from('components')
        .select('id, name, type, data_source, is_published, version, created_at, updated_at')
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      setComponents(data || [])
    } catch (err) {
      console.error('Error cargando componentes:', err)
      setError('Error al cargar los componentes')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`¿Estás seguro de eliminar el componente "${name}"?`)) return

    try {
      // @ts-ignore - TypeScript type issue with components table
      const { error: deleteError } = await supabase
        .from('components')
        .delete()
        .eq('id', id)

      if (deleteError) throw deleteError

      // Refresh list
      await loadComponents()
    } catch (err) {
      console.error('Error eliminando componente:', err)
      alert('Error al eliminar el componente')
    }
  }

  const handleTogglePublish = async (id: string, currentStatus: boolean) => {
    try {
      const query = supabase.from('components')
      const { error: updateError } = await query
        // @ts-ignore - TypeScript type issue with components table
        .update({ is_published: !currentStatus })
        .eq('id', id)

      if (updateError) throw updateError

      // Refresh list
      await loadComponents()
    } catch (err) {
      console.error('Error actualizando componente:', err)
      alert('Error al actualizar el componente')
    }
  }

  const getTypeIcon = (type: ComponentType) => {
    switch (type) {
      // Componentes originales
      case 'kpi': return '📊'
      case 'line-chart': return '📈'
      case 'bar-chart': return '📊'
      case 'table': return '📋'
      // Fase 1: Críticos
      case 'map': return '🗺️'
      case 'pie-chart': return '🥧'
      case 'gauge': return '🎯'
      // Fase 2: Muy útiles
      case 'area-chart': return '📈'
      case 'stacked-bar': return '📊'
      case 'scatter-plot': return '🔵'
      // Fase 3: Nice to have
      case 'heatmap': return '🔥'
      case 'treemap': return '🌳'
      case 'radar-chart': return '🎯'
      case 'radial-bar': return '🔵'
      default: return '❓'
    }
  }

  const getTypeLabel = (type: ComponentType) => {
    switch (type) {
      // Componentes originales
      case 'kpi': return 'KPI'
      case 'line-chart': return 'Gráfico de Línea'
      case 'bar-chart': return 'Gráfico de Barras'
      case 'table': return 'Tabla'
      // Fase 1: Críticos
      case 'map': return 'Mapa Interactivo'
      case 'pie-chart': return 'Gráfico Circular'
      case 'gauge': return 'Medidor'
      // Fase 2: Muy útiles
      case 'area-chart': return 'Gráfico de Área'
      case 'stacked-bar': return 'Barras Apiladas'
      case 'scatter-plot': return 'Gráfico de Dispersión'
      // Fase 3: Nice to have
      case 'heatmap': return 'Mapa de Calor'
      case 'treemap': return 'Mapa de Árbol'
      case 'radar-chart': return 'Gráfico Radial'
      case 'radial-bar': return 'Barras Radiales'
      default: return type
    }
  }

  const filteredComponents = components.filter((comp) => {
    if (filter === 'published' && !comp.is_published) return false
    if (filter === 'draft' && comp.is_published) return false
    if (typeFilter !== 'all' && comp.type !== typeFilter) return false

    const q = searchQuery.trim().toLowerCase()
    if (q) {
      const nameMatch = comp.name.toLowerCase().includes(q)
      const sourceMatch = (comp.data_source || '').toLowerCase().includes(q)
      const typeLabelMatch = getTypeLabel(comp.type).toLowerCase().includes(q)
      const typeIdMatch = comp.type.toLowerCase().includes(q)
      if (!nameMatch && !sourceMatch && !typeLabelMatch && !typeIdMatch) return false
    }

    return true
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando componentes...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700">{error}</p>
        <button
          onClick={loadComponents}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Reintentar
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Componentes Creados</h1>
          <p className="text-gray-600 mt-1">
            Gestiona todos los componentes del dashboard
          </p>
        </div>
        <button
          onClick={onCreateNew}
          className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center gap-2"
        >
          <span className="text-xl">+</span>
          Crear Componente
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-4 mb-6 space-y-4">
        <div>
          <label htmlFor="component-search" className="sr-only">
            Buscar componentes
          </label>
          <div className="relative max-w-xl">
            <span
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              aria-hidden
            >
              🔍
            </span>
            <input
              id="component-search"
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre, tipo o fuente de datos…"
              autoComplete="off"
              className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-10 text-sm text-gray-900 placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20"
            />
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Limpiar búsqueda"
              >
                ✕
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          {/* Status Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todos ({components.length})
            </button>
            <button
              onClick={() => setFilter('published')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'published'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Publicados ({components.filter(c => c.is_published).length})
            </button>
            <button
              onClick={() => setFilter('draft')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'draft'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Borradores ({components.filter(c => !c.is_published).length})
            </button>
          </div>

          <div className="border-l border-gray-300"></div>

          {/* Type Filter */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setTypeFilter('all')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todos los tipos
            </button>

            {/* Originales */}
            <button
              onClick={() => setTypeFilter('kpi')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'kpi'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📊 KPI
            </button>
            <button
              onClick={() => setTypeFilter('line-chart')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'line-chart'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📈 Línea
            </button>
            <button
              onClick={() => setTypeFilter('bar-chart')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'bar-chart'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📊 Barras
            </button>
            <button
              onClick={() => setTypeFilter('table')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'table'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📋 Tabla
            </button>

            {/* Fase 1: Críticos */}
            <button
              onClick={() => setTypeFilter('map')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'map'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🗺️ Mapa
            </button>
            <button
              onClick={() => setTypeFilter('pie-chart')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'pie-chart'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🥧 Circular
            </button>
            <button
              onClick={() => setTypeFilter('gauge')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'gauge'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🎯 Medidor
            </button>

            {/* Fase 2: Muy útiles */}
            <button
              onClick={() => setTypeFilter('area-chart')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'area-chart'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📈 Área
            </button>
            <button
              onClick={() => setTypeFilter('stacked-bar')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'stacked-bar'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📊 Apiladas
            </button>
            <button
              onClick={() => setTypeFilter('scatter-plot')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'scatter-plot'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🔵 Dispersión
            </button>

            {/* Fase 3: Nice to have */}
            <button
              onClick={() => setTypeFilter('heatmap')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'heatmap'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🔥 Heatmap
            </button>
            <button
              onClick={() => setTypeFilter('treemap')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'treemap'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🌳 Treemap
            </button>
            <button
              onClick={() => setTypeFilter('radar-chart')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'radar-chart'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🎯 Radial
            </button>
            <button
              onClick={() => setTypeFilter('radial-bar')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'radial-bar'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🔵 Radial Bar
            </button>
          </div>
        </div>
      </div>

      {/* Components Grid */}
      {filteredComponents.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            {components.length === 0
              ? 'No hay componentes'
              : searchQuery.trim()
                ? 'Sin resultados'
                : 'No hay componentes'}
          </h3>
          <p className="text-gray-500 mb-6">
            {components.length === 0
              ? filter === 'all'
                ? 'Aún no has creado ningún componente. ¡Crea tu primer componente ahora!'
                : `No hay componentes ${filter === 'published' ? 'publicados' : 'en borrador'}`
              : searchQuery.trim()
                ? 'Prueba con otras palabras o limpia los filtros y la búsqueda.'
                : filter === 'all'
                  ? 'No hay componentes que coincidan con los filtros seleccionados.'
                  : `No hay componentes ${filter === 'published' ? 'publicados' : 'en borrador'} con estos filtros.`}
          </p>
          <button
            onClick={onCreateNew}
            className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
          >
            Crear Primer Componente
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredComponents.map((component) => (
            <div
              key={component.id}
              className="bg-white border rounded-lg p-6 hover:shadow-lg transition-shadow"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{getTypeIcon(component.type)}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">{component.name}</h3>
                    <p className="text-sm text-gray-500">{getTypeLabel(component.type)}</p>
                  </div>
                </div>
                <div>
                  {component.is_published ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                      Publicado
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                      Borrador
                    </span>
                  )}
                </div>
              </div>

              {/* Details */}
              <div className="space-y-2 mb-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Fuente:</span>
                  <span className="font-medium text-gray-900">{component.data_source}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Versión:</span>
                  <span className="font-medium text-gray-900">v{component.version}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Creado:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(component.created_at).toLocaleDateString('es-ES')}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => onEditComponent(component.id)}
                  className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors"
                >
                  ✏️ Editar
                </button>
                <button
                  onClick={() => handleTogglePublish(component.id, component.is_published)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    component.is_published
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {component.is_published ? '📤 Despublicar' : '📢 Publicar'}
                </button>
                <button
                  onClick={() => handleDelete(component.id, component.name)}
                  className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                >
                  🗑️ Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats Footer */}
      {components.length > 0 && (
        <div className="mt-8 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Estadísticas</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{components.length}</div>
              <div className="text-sm text-gray-600">Total Componentes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {components.filter(c => c.is_published).length}
              </div>
              <div className="text-sm text-gray-600">Publicados</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {components.filter(c => c.type === 'kpi').length}
              </div>
              <div className="text-sm text-gray-600">KPIs</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {components.filter(c => c.type === 'line-chart' || c.type === 'bar-chart').length}
              </div>
              <div className="text-sm text-gray-600">Gráficos</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
